// Barras horizontales con etiqueta directa (HU-34b, `ui/MG-ILE-1092.md`, «Gráficos: forma elegida»).
// Presentación pura: una lista en la que cada ítem dice su etiqueta y su cifra en texto, y debajo
// una barra proporcional al mayor de la lista. La usan «Distribución por estado» y «Casos por país».
//
// Sin estilos en línea ni colores literales (§6): la barra es un `svg` de 8 px con un `rect` de
// fondo (`fill-hairline`) y otro con `width` en porcentaje como atributo SVG, en `fill-marca`. Un
// solo color: lo que distingue cada ítem es el texto. Con un valor en 0 se dibuja solo el fondo, y
// con toda la lista en 0 no se divide. El dibujo es `aria-hidden`: el ítem ya se lee «Perú, 20».

import type { ReactNode } from 'react';

export type BarraHorizontal = {
  /** La clave del ítem (`en_evaluacion`, `PE`): va al atributo `data-<nombreDelDato>`. */
  clave: string;
  etiqueta: ReactNode;
  valor: number;
  /** Lo que va antes de la etiqueta (una bandera). */
  antes?: ReactNode;
};

export default function BarrasHorizontales({ barras, nombreDelDato, testidDeBarra, 'data-testid': testid, 'aria-labelledby': etiquetadoPor }: Readonly<{
  barras: BarraHorizontal[];
  /** El nombre del atributo `data-*` que lleva la clave de cada ítem («estado» → `data-estado`). */
  nombreDelDato: string;
  testidDeBarra?: string;
  'data-testid'?: string;
  /** El id del título que nombra el gráfico (el de su tarjeta). */
  'aria-labelledby'?: string;
}>) {
  const mayor = Math.max(0, ...barras.map((b) => b.valor));
  return (
    <figure data-testid={testid} aria-labelledby={etiquetadoPor} className="m-0">
      <ul className="flex flex-col gap-3 list-none m-0 p-0">
        {barras.map((b) => {
          const ancho = mayor > 0 ? (Math.max(b.valor, 0) / mayor) * 100 : 0;
          return (
            <li key={b.clave} data-testid={testidDeBarra} {...{ [`data-${nombreDelDato}`]: b.clave }} className="flex flex-col gap-1">
              <span className="flex items-center justify-between gap-3 text-body text-ink">
                <span className="inline-flex min-w-0 items-center gap-2">
                  {b.antes}
                  <span className="truncate">{b.etiqueta}</span>
                </span>
                {/* La coma solo se lee: el ítem se anuncia «En evaluación, 4». */}
                <span className="sr-only">, </span>
                <span className="tabular-nums slashed-zero text-ink">{b.valor}</span>
              </span>
              <svg aria-hidden="true" focusable="false" className="h-2 w-full">
                <rect className="fill-hairline" width="100%" height="100%" rx="4" />
                {ancho > 0 && <rect className="fill-marca" width={`${ancho}%`} height="100%" rx="4" />}
              </svg>
            </li>
          );
        })}
      </ul>
    </figure>
  );
}
